import React, { useCallback, useEffect, useState } from 'react';
import { ClipboardCheck } from 'lucide-react';
import SafetyNotice from '../SafetyNotice';
import RestorationChecklist from './RestorationChecklist';
import LessonsLearnedSection from './LessonsLearnedSection';
import SignOffSection from './SignOffSection';
import { jobBriefApi } from '../../../services/jobBriefApi';

/**
 * CloseoutPhase — Phase E composer.
 *
 * Three sections + PDF export. Once close-out is completed (at least one
 * sign-off + Complete button pressed), all writer controls hide (isReadOnly
 * propagates), but the sections and the PDF button remain visible.
 */
export default function CloseoutPhase({ draft }) {
  const draftId = draft?.id;
  const ownerEmail = draft?.owner_email;
  const [state, setState] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    if (!draftId) return;
    try {
      const s = await jobBriefApi.getCloseoutState(draftId);
      setState(s);
    } finally {
      setLoading(false);
    }
  }, [draftId]);

  useEffect(() => { refresh(); }, [refresh]);

  const readOnly = !!state?.completed_at;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }} data-testid="jbv2-closeout-phase">
      <SafetyNotice />

      {/* Live counters strip */}
      <div
        style={{
          padding: '10px 14px', borderRadius: 12,
          background: 'rgba(15,27,70,0.65)',
          border: '1px solid rgba(167,139,250,0.28)',
          display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap',
        }}
        data-testid="jbv2-closeout-activity-strip"
      >
        <ClipboardCheck size={16} color="#c4b5fd" />
        <span style={{ color: '#94a3b8', fontSize: 11, letterSpacing: '0.10em', textTransform: 'uppercase', fontWeight: 800 }}>
          Close-out progress
        </span>
        <span className="jbv2-hp-row-meta">
          Restoration:{' '}
          <strong data-testid="jbv2-closeout-count-restoration">
            {state?.counts?.restoration_completed ?? 0}/{state?.counts?.restoration_total ?? 0}
          </strong>
        </span>
        <span className="jbv2-hp-row-meta">
          · Lessons: <strong data-testid="jbv2-closeout-count-lessons">{state?.counts?.lessons ?? 0}</strong>
        </span>
        <span className="jbv2-hp-row-meta">
          · Sign-offs: <strong data-testid="jbv2-closeout-count-signoffs">{state?.counts?.signoffs ?? 0}</strong>
        </span>
        {loading && <span className="jbv2-hp-row-meta" style={{ marginLeft: 'auto' }}>Loading…</span>}
      </div>

      <RestorationChecklist
        draftId={draftId}
        closeoutState={state}
        onChanged={refresh}
        isReadOnly={readOnly}
      />
      <LessonsLearnedSection
        draftId={draftId}
        closeoutState={state}
        ownerEmail={ownerEmail}
        onChanged={refresh}
        isReadOnly={readOnly}
      />
      <SignOffSection
        draftId={draftId}
        closeoutState={state}
        ownerEmail={ownerEmail}
        onChanged={refresh}
        isReadOnly={readOnly}
      />
    </div>
  );
}
